import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { currencyAPI } from '../services/currencyAPI';

const Pricing = () => {
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);
  const [formattedPrices, setFormattedPrices] = useState({});

  const plans = useMemo(() => [
    {
      name: "Starter",
      price: "$99",
      period: "/month",
      description: "Perfect for small businesses getting started",
      features: [
        "Up to 1,000 products monitored",
        "Basic marketplace monitoring",
        "Email alerts",
        "Monthly reports",
        "Email support"
      ]
    },
    {
      name: "Professional", 
      price: "$299",
      period: "/month",
      description: "Advanced features for growing businesses",
      popular: true,
      features: [
        "Up to 10,000 products monitored",
        "All marketplace monitoring",
        "Real-time alerts",
        "Dynamic pricing automation",
        "MAP policy enforcement",
        "Weekly reports",
        "Priority support"
      ]
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "Tailored solutions for large organizations",
      features: [
        "Unlimited products monitored",
        "Custom integrations",
        "Advanced analytics",
        "Dedicated account manager",
        "24/7 phone support",
        "Custom reporting",
        "SLA guarantee"
      ]
    }
  ], []);

  const formatPrice = useCallback(async (usdPrice) => {
    if (usdPrice === 'Custom') return 'Custom';
    const usdAmount = parseInt(usdPrice.replace('$', ''));
    
    if (currency === 'USD') {
      return `$${usdAmount}`;
    }
    
    try {
      const result = await currencyAPI.convertCurrency('USD', currency, usdAmount);
      const symbols = { USD: '$', INR: '₹', GBP: '£', EUR: '€' };
      return `${symbols[currency]}${Math.round(result.converted_amount)}`;
    } catch (error) {
      const fallbackRates = { USD: 1, INR: 83, GBP: 0.79, EUR: 0.92 };
      const fallbackRate = fallbackRates[currency] || 1;
      const symbols = { USD: '$', INR: '₹', GBP: '£', EUR: '€' };
      return `${symbols[currency]}${Math.round(usdAmount * fallbackRate)}`;
    }
  }, [currency]);

  useEffect(() => {
    const updatePrices = async () => {
      setLoading(true);
      const newPrices = {};
      for (const plan of plans) {
        newPrices[plan.name] = await formatPrice(plan.price);
      }
      setFormattedPrices(newPrices);
      setLoading(false);
    };
    
    updatePrices();
  }, [currency, formatPrice, plans]);

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              <span className="gradient-text">Simple, Transparent</span>
              <br />Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Choose the perfect plan for your business. All plans include a 30-day free trial.
            </p>
            
            <div className="flex justify-center space-x-2">
              {['USD', 'INR', 'GBP', 'EUR'].map((curr) => (
                <button
                  key={curr}
                  onClick={() => setCurrency(curr)}
                  disabled={loading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    currency === curr 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {curr}
                </button>
              ))}
            </div>
            {loading && (
              <p className="text-sm text-gray-500 mt-2">Loading current exchange rates...</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div key={index} className={`card relative flex flex-col ${plan.popular ? 'ring-2 ring-orange-500' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">{formattedPrices[plan.name] || plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <Link 
                    to="/trial" 
                    className={`w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                      plan.popular 
                        ? 'btn-primary' 
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-4">
              Need a custom solution? We're here to help.
            </p>
            <Link to="/contact" className="btn-secondary">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;